import { Button, Col, Form, Modal, Row } from "react-bootstrap"
import i18next from "i18next"
import { useStellantisActions, useStellantisState } from "src/store/StellantisStore";

// @ts-ignore
import './StellantisRequestQuoteModal.scss';
import { Formik } from "formik";
import { useEffect, useState } from "react";
import { useApiClient } from "src/hooks/useApiClient";
import { STELLANTIS_MARCHE } from "src/utils/constants";
import SpinnerButton from "../SpinnerButton/SpinnerButton";
import * as StellantisConstant from './stellantisConstants';
import * as StellantisUtils from './stellantisUtils';
import { StellantisFormFields, StellantisTracking, StellantisValidation } from ".";
import { GTMsendEvent } from "src/utils/trackingGA4";
import { MotornetSelectedVehicle } from "../../motornet/common/MotornetVehicleSelector";
import toaster from "src/utils/toaster";
import { useErrorToast } from "src/hooks/useErrorToast";
import Btn from "../Btn/Btn";
import { RoleEnum } from "src/api";
import { useAuth } from "src/hooks/useAuth";

type Props = {}

const StellantisRequestQuoteModal: React.FC<Props> = ({ }) => {
  const { userRole } = useAuth();
  const { closeRequestQuoteModal } = useStellantisActions();
  const { isEnabled, isRequestQuoteOpen } = useStellantisState();
  const { apiClient, handleApiResponse } = useApiClient();
  const [selectedVehicle, setSelectedVehicle] = useState<MotornetSelectedVehicle>({ brand: null, model: null, version: null })
  const [isCommercialVehicle, setIsCommercialVehicle] = useState(false);
  const [pendingCreation, setPendingCreation] = useState(false);
  const { showError } = useErrorToast();
  const [initialValues, setInitialValues] = useState<any>(
    StellantisConstant.makeDefaultQuoteRequestValues()
  );

  useEffect(() => {
    return () => {
      setSelectedVehicle({ brand: null, model: null, version: null })
    }
  }, []);

  // Vehicle specs (alimentazione/cambio/cavalli/...) and images are denormalized onto
  // `values` by the MotornetVehicle field when a version is picked, so they submit as-is.
  const buildPayload = (values: any) => {
    return {
      ...values,
      canone: StellantisUtils.isNilOrEmpty(values.canone) ? null : values.canone,
      autoSostitutivaGruppo: StellantisUtils.isNilOrEmpty(values.autoSostitutivaGruppo) ? null : values.autoSostitutivaGruppo,
      cognome: StellantisUtils.isNilOrEmpty(values.cognome) ? null : values.cognome,
      nome: StellantisUtils.isNilOrEmpty(values.nome) ? null : values.nome,
      ragioneSociale: StellantisUtils.isNilOrEmpty(values.ragioneSociale) ? null : values.ragioneSociale,
      tipoGomma: StellantisUtils.isNilOrEmpty(values.tipoGomma) ? null : values.tipoGomma,
      requestNote: StellantisUtils.isNilOrEmpty(values.requestNote) ? null : values.requestNote,
      optionals: values.optionals?.filter(opt => !StellantisUtils.isNilOrEmpty(opt.descrizione)) || [],
      images: values.images || [],
    };
  }

  const handleSubmit = async (values) => {
    try {
      setPendingCreation(true);
      const payload = buildPayload(values);
      const isMarcaStellantis = STELLANTIS_MARCHE.includes(payload.marca?.toUpperCase());

      await handleApiResponse(apiClient.stellantisControllerService.createQuoteRequest(payload));

      try {
        GTMsendEvent({
          event: 'complete_quote_request',
          evtCat: 'quote_request',
          ...StellantisTracking.buildQuoteRequestGtmPayload(values),
        });
      } catch (error) {
        console.warn('[Horizon] Failed to send Stellantis quote request GTM event.', error);
      }

      const successMessage = (isMarcaStellantis || RoleEnum.SEGNALATORE === userRole)
        ? i18next.t('quote_request_create_success')
        : i18next.t('quote_request_create_success_no_stellantis');

      // Il modale resta aperto: l'utente puo' modificare i parametri e inviare un'altra richiesta.
      toaster.success(successMessage, { delay: 500 });
    } catch (error) {
      showError(error, i18next.t('quote_request_create_failure'));
    } finally {
      setPendingCreation(false);
    }
  };

  const onCloseModal = () => {
    setSelectedVehicle({ brand: null, model: null, version: null })
    setInitialValues(StellantisConstant.makeDefaultQuoteRequestValues());
    closeRequestQuoteModal();
  }

  if (!isEnabled) return null;

  return (
    <Modal
      show={isRequestQuoteOpen}
      onHide={onCloseModal}
      size="xl"
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header closeButton>
        <Modal.Title className='display-6'>{i18next.t("request_quote")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Formik
          initialValues={initialValues}
          onSubmit={handleSubmit}
          validationSchema={StellantisValidation.createRequestSchema}
          enableReinitialize={true}
        >
          {({ values, handleSubmit }) => {
            return (
              <Form className="p-0" onSubmit={handleSubmit}>
                <Row>
                  <Col sm={values.tipoCliente === 'azienda' ? 6 : 4}>
                    <StellantisFormFields.TipoCliente />
                  </Col>
                  {['libero_professionista', 'privato'].includes(values.tipoCliente) && (
                    <>
                      <Col sm={4}>
                        <StellantisFormFields.Nome />
                      </Col>
                      <Col sm={4}>
                        <StellantisFormFields.Cognome />
                      </Col>
                    </>
                  )}
                  {values.tipoCliente === 'azienda' && (
                    <Col sm={6}>
                      <StellantisFormFields.RagioneSociale />
                    </Col>
                  )}
                </Row>

                <Row>
                  <StellantisFormFields.MotornetVehicle
                    selectedVehicle={selectedVehicle}
                    setSelectedVehicle={setSelectedVehicle}
                    onCommercialChange={setIsCommercialVehicle}
                  />
                </Row>

                <Row className="mt-3">
                  <Col sm={4}>
                    <StellantisFormFields.KmAnnui />
                  </Col>
                  <Col sm={4}>
                    <StellantisFormFields.Durata />
                  </Col>
                  <Col sm={4}>
                    <StellantisFormFields.AnticipoNoIva />
                  </Col>
                </Row>

                <Row className="mt-2">
                  <Col sm={4}>
                    <StellantisFormFields.Rca />
                  </Col>
                  <Col sm={4}>
                    <StellantisFormFields.KaskoFranchigia />
                  </Col>
                  <Col sm={4}>
                    <StellantisFormFields.IncendioFurtoScopertoPerc />
                  </Col>
                </Row>

                <hr />
                <Row>
                  <Col md={6}>
                    <StellantisFormFields.GommeYN />
                  </Col>
                  {values.gommeYN && (
                    <>
                      <Row md={12} className="mt-2">
                        <Col sm={4}>
                          <StellantisFormFields.TipoGomma />
                        </Col>
                        <Col sm={4}>
                          <StellantisFormFields.NumTrenoGommeEstive />
                        </Col>
                        <Col sm={4}>
                          <StellantisFormFields.NumTrenoGommeInvernali />
                        </Col>
                      </Row>
                      <Row md={12}>
                        <StellantisFormFields.GommeIllimitate />
                      </Row>
                    </>)
                  }
                </Row>
                <hr />
                <Row>
                  <Col md={6}>
                    <StellantisFormFields.AutoSostitutiva />
                  </Col>
                </Row>
                {values.autoSostitutivaYN && <Row className="mt-2">
                  <Col md={4}>
                    <StellantisFormFields.GruppoAutoSostitutiva isCommercialVehicle={isCommercialVehicle} />
                  </Col>
                </Row>}
                <hr />
                <Row>
                  <StellantisFormFields.RequestOptionals />
                </Row>
                <hr />
                <Row>
                  <Col>
                    <StellantisFormFields.RequestNote />
                  </Col>
                </Row>
                <hr />
                <Row className="mt-3">
                  <Col sm={4}>
                    <StellantisFormFields.Provvigioni />
                  </Col>
                </Row>
                <Row className="mt-4">
                  <Col md={12} className="d-flex justify-content-end">
                    <Button variant="link" onClick={onCloseModal} disabled={pendingCreation}>
                      <SpinnerButton label={i18next.t('close')} />
                    </Button>
                    <Btn
                      type="submit"
                      // onClick={() => console.log("here", values)}
                      icon="arrow-right"
                      disabled={pendingCreation}
                    >
                      <SpinnerButton
                        busy={pendingCreation}
                        label={i18next.t('send')}
                      />
                    </Btn>
                  </Col>
                </Row>
              </Form>
            );
          }}
        </Formik>
      </Modal.Body>
    </Modal>
  )
}


export default StellantisRequestQuoteModal
